import { createI18n, type Leaves } from "@bridewell/shared";
import { currentLocale, LOCALE_TAG, LOCALES } from "@/lib/locales";
import { en, type Dictionary } from "./en";

export type TKey = Leaves<Dictionary>;

/**
 * Translation tables of the public pages' languages (src/lib/locales.ts), keyed by BCP 47 tag: empty until `loadLocale`
 * (./load-locale.ts) fills one before a translated page renders. Only keys those pages show are translated; any other
 * key falls back to English, and the app itself is English.
 */
export const translations: Record<string, Partial<Record<TKey, string>>> = Object.fromEntries(LOCALES.map((l) => [LOCALE_TAG[l], {}]));

/** The seeds (checklist tasks, budget lines, day-of events) and the romantic quotes in each language, from the Apple app's own translations. */
export const seedTranslations: Record<string, Record<string, string>> = Object.fromEntries(LOCALES.map((l) => [LOCALE_TAG[l], {}]));

// A page's language is its URL prefix (/de/…), never the browser's, so every visitor and every search engine sees the
// same language at the same address. The lookup, fallback and `{n}` interpolation are shared with the Android app
// (packages/shared/src/i18n.ts).
const i18n = createI18n({ source: en, translations: translations as Record<string, Record<string, string>>, locale: () => LOCALE_TAG[currentLocale()] });

/** `t("modules.guests")` → "Guests". `{n}` placeholders are replaced from `vars`. */
export const t = i18n.t;

/** Enum label lookup with a safe fallback to the raw value. */
export const label = i18n.label;

/** A seed's title (`"Book venue"`) in the page's language; the English title where there is no translation. */
export const seedLabel = (title: string): string => seedTranslations[LOCALE_TAG[currentLocale()]]?.[title] ?? title;
